import React, { useEffect, useMemo, useState } from 'react'
import type { INotebookContent } from '@jupyterlab/nbformat'
import { ArrowsPointingOutIcon, ArrowsPointingInIcon } from '@heroicons/react/24/outline'
import { ChallengeService, PartnerService, UserService } from '@duck/api-definitions'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    Card,
    CardContent,
    IconTooltip,
    Tabs,
    TabsList,
    TabsTrigger,
    TabsContent,
    MarkdownEditor,
    Button,
    cn,
} from '@duck/ui'
import { useTar, TranslationKey } from '@/modules/i18n'
import { RelativeDateTime } from '@/components/datetime/RelativeDateTime'
import { GradeBadge } from '@/components/GradeBadge/GradeBadge'
import { UserBadge } from '@/components/UserBadge/UserBadge'
import { WysiwygEditor } from '@components/WysiwygEditor'
import { ReadOnlyPythonNotebook } from '@components/PythonNotebook/ReadOnlyPythonNotebook'
import { getContentChartIds } from '@components/WysiwygEditor/wysiwygUtils'
import { DownloadPDFButton } from '@/components/DownloadPDFButton/DownloadPDFButton'
import { formatWorkTime } from '@/utils/time.utils'

export interface ResultsDetails {
    personName: string
    personUserId?: string
    personFirstName?: string
    personLastName?: string
    personEmail?: string
    feedback: string
    startDate?: string
    submitDate?: string
    grade?: number
    solution?: string
    workTime?: number
    charts?: ChallengeService.WorkState['charts']
    queries?: PartnerService.AssignmentDetailsQueries
    challengeId?: string
    chosenWeapon?: ChallengeService.TaskWeaponsOfChoiceEnum
    jNotebook?: PartnerService.AssignmentDetails['jNotebook']
}

export const toResultsDetails = (
    d: PartnerService.AssignmentDetails,
    personName: string,
    person?: UserService.Person
): ResultsDetails => ({
    personName,
    personUserId: person?.userId,
    personFirstName: person?.firstName,
    personLastName: person?.lastName,
    personEmail: person?.email,
    feedback: d.feedback || '',
    startDate: d.startDate,
    submitDate: d.submitDate,
    workTime: d.workTime,
    grade: d.grade,
    solution: d.solution,
    charts: d.charts,
    queries: d.queries,
    challengeId: d.challengeId,
    chosenWeapon: d.chosenWeapon,
    jNotebook: d.jNotebook,
})

const QueriesTabContent: React.FC<{ queries: PartnerService.AssignmentDetailsQueries }> = ({ queries }) => {
    const { t } = useTar()
    const total = queries.totalCount ?? queries.last20?.length ?? 0
    const entries = queries.last20 ?? []

    return (
        <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
                {t('challenges.queriesTotal' as TranslationKey, { count: total })}
            </p>
            <ul className="m-0 list-none space-y-3 p-0">
                {entries.map((entry, i) => (
                    <li key={i} className="overflow-hidden rounded-md border border-border bg-muted/10">
                        <div className="p-3">
                            <pre
                                className="max-h-32 overflow-x-auto overflow-y-auto whitespace-pre-wrap break-all bg-card font-mono text-xs text-foreground custom-scrollbar"
                                dir="ltr"
                                title={entry.query}
                            >
                                {entry.query}
                            </pre>
                            {(entry.resultCount !== undefined || entry.errorCode) && (
                                <div className="mt-2 flex flex-wrap gap-2 text-xs text-muted-foreground">
                                    {entry.resultCount !== undefined && (
                                        <span>{t('challenges.queryResultCount' as TranslationKey, { count: entry.resultCount })}</span>
                                    )}
                                    {entry.errorCode && (
                                        <span className="text-destructive">{t('challenges.queryError' as TranslationKey)}: {entry.errorCode}</span>
                                    )}
                                </div>
                            )}
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    )
}

interface SubmissionResultsDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    details: ResultsDetails | null
}

export const SubmissionResultsDialog: React.FC<SubmissionResultsDialogProps> = ({ open, onOpenChange, details }) => {
    const { t } = useTar()
    const [isMaximized, setIsMaximized] = useState(false)

    useEffect(() => {
        if (open) {
            setIsMaximized(false)
        }
    }, [open])

    const solutionCharts = useMemo(() => {
        if (!details?.solution || !details?.charts) return {}
        const tmp = document.createElement('div')
        tmp.innerHTML = details.solution
        const ids = getContentChartIds(tmp)
        const filtered: ChallengeService.WorkState['charts'] = {}
        ids.forEach((id) => {
            if (details.charts?.[id]) filtered[id] = details.charts[id]
        })
        return filtered
    }, [details?.solution, details?.charts])

    if (!details) return null
    const isPython = details.chosenWeapon === ChallengeService.TaskWeaponsOfChoiceEnum.Python
    const hasQueries = !isPython && !!details.queries?.last20?.length
    const hasSolution = isPython ? !!details.jNotebook : !!details.solution
    const tabCount = 1 + (hasSolution ? 1 : 0) + (hasQueries ? 1 : 0)

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                width="md"
                className={cn(
                    'overflow-hidden',
                    isMaximized &&
                        'sm:inset-0 sm:translate-x-0 sm:translate-y-0 sm:max-w-none sm:h-screen sm:max-h-screen sm:rounded-none sm:border-0',
                )}
                noMinHeight
            >
                <button
                    type="button"
                    onClick={() => setIsMaximized((prev) => !prev)}
                    aria-label={t(isMaximized ? 'common.minimize' : 'common.maximize')}
                    className="absolute top-4 right-12 rtl:left-12 rtl:right-auto z-10 hidden rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:block"
                >
                    {isMaximized ? <ArrowsPointingInIcon className="h-5 w-5" /> : <ArrowsPointingOutIcon className="h-5 w-5" />}
                </button>

                <DialogHeader>
                    <div className="flex min-w-0 flex-col items-start gap-1.5">
                        <DialogTitle>{t('challenges.submissionSummary')}</DialogTitle>
                        <div className="mt-3 flex w-full flex-wrap items-center justify-between gap-x-3 gap-y-1">
                            <UserBadge
                                userId={details.personUserId ?? ''}
                                firstName={details.personFirstName}
                                lastName={details.personLastName}
                                fullName={details.personName}
                                email={details.personEmail}
                            />
                            {details.challengeId && (
                                <DownloadPDFButton
                                    variant="link"
                                    challengeId={details.challengeId}
                                    disabled={!details.feedback}
                                />
                            )}
                        </div>
                    </div>
                </DialogHeader>

                <div className="mb-4 space-y-3 overflow-y-auto pe-2 custom-scrollbar">
                    <Card>
                        <CardContent className="p-4">
                            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                                <div>
                                    <p className="mb-1 text-xs text-muted-foreground">{t('common.started')}</p>
                                    {details.startDate ? (
                                        <RelativeDateTime isoDateString={details.startDate} className="text-sm font-medium" />
                                    ) : (
                                        <p className="text-sm text-muted-foreground">-</p>
                                    )}
                                </div>
                                <div>
                                    <p className="mb-1 text-xs text-muted-foreground">{t('common.submitted')}</p>
                                    {details.submitDate ? (
                                        <RelativeDateTime isoDateString={details.submitDate} className="text-sm font-medium" />
                                    ) : (
                                        <p className="text-sm text-muted-foreground">-</p>
                                    )}
                                </div>
                                <div>
                                    <div className="mb-1 flex items-center gap-1 text-xs text-muted-foreground">
                                        {t('common.actualWorkTime')}
                                        <IconTooltip content={t('common.actualWorkTimeTooltip')} />
                                    </div>
                                    <p className="text-sm font-medium">
                                        {details.workTime !== undefined ? formatWorkTime(details.workTime, t) : '-'}
                                    </p>
                                </div>
                                <div>
                                    <p className="mb-1 text-xs text-muted-foreground">{t('common.grade')}</p>
                                    {details.grade !== undefined ? (
                                        <GradeBadge grade={details.grade} small />
                                    ) : (
                                        <p className="text-sm text-muted-foreground">-</p>
                                    )}
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Tabs defaultValue="feedback" className="w-full">
                        <TabsList
                            className={cn(
                                'grid w-full',
                                tabCount === 3 ? 'grid-cols-3' : tabCount === 2 ? 'grid-cols-2' : 'grid-cols-1',
                            )}
                        >
                            <TabsTrigger value="feedback" disabled={!details.feedback}>
                                {t('challenges.aiFeedback')}
                            </TabsTrigger>
                            {hasSolution && (
                                <TabsTrigger value="solution">
                                    {t(isPython ? 'challenges.submittedNotebook' : 'challenges.submittedSolution')}
                                </TabsTrigger>
                            )}
                            {hasQueries && (
                                <TabsTrigger value="queries">{t('challenges.queries' as TranslationKey)}</TabsTrigger>
                            )}
                        </TabsList>
                        <TabsContent value="feedback" className="mt-3">
                            {details.feedback && (
                                <MarkdownEditor defaultValue={details.feedback} className="h-full w-full" mode="view" />
                            )}
                        </TabsContent>
                        {hasSolution && (
                            <TabsContent value="solution" className="mt-3">
                                <div className="rounded-md border border-border p-4">
                                    {isPython ? (
                                        <ReadOnlyPythonNotebook notebook={details.jNotebook as INotebookContent | undefined} />
                                    ) : (
                                        <WysiwygEditor content={details.solution || ''} startingCharts={solutionCharts} readOnly />
                                    )}
                                </div>
                            </TabsContent>
                        )}
                        {hasQueries && (
                            <TabsContent value="queries" className="mt-3">
                                {details.queries && <QueriesTabContent queries={details.queries} />}
                            </TabsContent>
                        )}
                    </Tabs>
                </div>

                <div className="flex justify-end">
                    <Button onClick={() => onOpenChange(false)}>{t('common.close')}</Button>
                </div>
            </DialogContent>
        </Dialog>
    )
}
